import { useState, useEffect, useMemo } from "react"
import { useLocation, useSearch } from "wouter"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { useToast } from "@/hooks/use-toast"
import { useInfiniteQuery, useQuery, useMutation, useQueryClient, type InfiniteData } from "@tanstack/react-query"
import { apiRequest } from "@/lib/queryClient"
import type { RepurposingContent, PillarPost } from "@shared/schema"
import {
  Link2,
  Sparkles,
  Filter,
  ExternalLink,
  Eye,
  Loader2,
  Youtube,
  FileText,
  Globe,
  Trash2,
  ArrowUpDown,
  Plus,
} from "lucide-react"
import { FloatingCard } from "@/components/FloatingCard"
import { AnimatedSection } from "@/components/AnimatedSection"
import { NewsPostsModal } from "@/components/NewsPostsModal"
import { RepurposingUrlModal } from "@/components/RepurposingUrlModal"
import { ScheduledPlanLink } from "@/components/ScheduledPlanLink"
import { StatCard } from "@/components/StatCard"
import { statusConfig } from "@/lib/status-config"

// Server shape of GET /api/repurposing-content — item status is DERIVED from the item's
// generated posts (≥1 posted/scheduled → "approved"; DFY rows from their pillar-post
// lifecycle); dates arrive as ISO strings.
type RepurposingListItem = RepurposingContent & {
  isDfy: boolean
  postsCount: number
  derivedStatus: "pending" | "approved"
  nextScheduledFor: string | null
  nextScheduledPostId: string | null
}

interface RepurposingListResponse {
  items: RepurposingListItem[]
  total: number
  stats: { total: number; pending: number; approved: number; dfy: number }
}

type StatusFilter = "all" | "pending" | "approved"
type SortOrder = "newest" | "oldest"

const PAGE_SIZE = 20

export default function RepurposingContent() {
  const [selectedStatus, setSelectedStatus] = useState<StatusFilter>("all")
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest")
  const [selectedContent, setSelectedContent] = useState<RepurposingListItem | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isUrlModalOpen, setIsUrlModalOpen] = useState(false)
  const [deleteConfirmContent, setDeleteConfirmContent] = useState<RepurposingListItem | null>(null)
  // DFY (Lina) items open the shared modal read-only via sourceType="pillar_post"; hydrate the
  // full pillar row on click since the list item doesn't carry postContent/hashtags.
  const [dfyPost, setDfyPost] = useState<PillarPost | null>(null)
  const [isDfyModalOpen, setIsDfyModalOpen] = useState(false)
  const [openingDfyId, setOpeningDfyId] = useState<string | null>(null)
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [, setLocation] = useLocation()

  // Deep-link from the Posting-Plan: ?item=<contentId>&platform=<tab> opens the exact
  // post in the Post-Vorschau modal (industry-news `?news=` parity).
  const search = useSearch()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])
  const deepLinkItemId = searchParams.get("item")
  const deepLinkPlatform = searchParams.get("platform") ?? undefined

  const { data: deepLinkedItem, error: deepLinkError } = useQuery<RepurposingListItem>({
    queryKey: ['/api/repurposing-content/item', deepLinkItemId],
    queryFn: () => apiRequest('GET', `/api/repurposing-content/${deepLinkItemId}`),
    enabled: !!deepLinkItemId,
  })

  useEffect(() => {
    if (deepLinkedItem) {
      setSelectedContent(deepLinkedItem)
      setIsModalOpen(true)
    }
  }, [deepLinkedItem])

  // A dead/foreign id must not fail silently (retry is globally off): say so and clean
  // the URL, otherwise ?item= sticks forever because the modal never opens/closes.
  useEffect(() => {
    if (deepLinkError && deepLinkItemId) {
      toast({
        title: "Inhalt nicht gefunden",
        description: "Der verlinkte Inhalt existiert nicht mehr.",
        variant: "destructive",
      })
      setLocation("/repurposing", { replace: true })
    }
  }, [deepLinkError, deepLinkItemId, toast, setLocation])

  const deleteMutation = useMutation({
    mutationFn: async (contentId: string) => {
      await apiRequest('DELETE', `/api/repurposing-content/${contentId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/repurposing-content'] })
      toast({
        title: "Gelöscht",
        description: "Der Inhalt wurde gelöscht."
      })
      setDeleteConfirmContent(null)
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message || "Löschen fehlgeschlagen.",
        variant: "destructive"
      })
    }
  })

  const handleViewPosts = (content: RepurposingListItem) => {
    setSelectedContent(content)
    setIsModalOpen(true)
  }

  // DFY item → hydrate the full pillar row, then open the shared modal read-only.
  const openDfyPost = async (id: string) => {
    setOpeningDfyId(id)
    try {
      const post = await apiRequest('GET', `/api/pillar-posts/${id}`)
      setDfyPost(post as PillarPost)
      setIsDfyModalOpen(true)
    } catch (err: any) {
      toast({
        title: "Post konnte nicht geladen werden",
        description: err?.data?.error || err?.message || "Bitte versuchen Sie es später erneut.",
        variant: "destructive",
      })
    } finally {
      setOpeningDfyId(null)
    }
  }

  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open)
    if (!open) {
      setSelectedContent(null) // Clear selected content when modal closes to prevent stale content
      if (deepLinkItemId) {
        setLocation("/repurposing", { replace: true }) // drop ?item= so it doesn't reopen
      }
    }
  }

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['/api/repurposing-content', selectedStatus, sortOrder],
    queryFn: async ({ pageParam }): Promise<RepurposingListResponse> => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(pageParam) })
      if (selectedStatus !== 'all') params.set('status', selectedStatus)
      if (sortOrder !== 'newest') params.set('sort', sortOrder)
      // apiRequest (not bare fetch): errors keep .status/.data so the global 402/403
      // access gate in queryClient.ts sees them like every other query's
      return apiRequest('GET', `/api/repurposing-content?${params.toString()}`)
    },
    initialPageParam: 0,
    // Global defaults set staleTime: Infinity, so refetchOnWindowFocus: true would never
    // fire (v5 only refetches STALE queries on focus). 'always' bypasses staleness so the
    // stats + n8n results actually refresh when the user returns to the tab.
    refetchOnWindowFocus: 'always',
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0)
      return loaded < lastPage.total ? loaded : undefined
    },
    // Poll only while a visible self-serve item is still waiting for its posts from n8n;
    // stops by itself once every item has posts (plus refetch on window focus).
    // DFY rows are their own post (postsCount is always 1), so they never hold the poll open.
    refetchInterval: (query) => {
      const pages = (query.state.data as InfiniteData<RepurposingListResponse> | undefined)?.pages
      const waiting = pages?.some(page => page.items.some(item => !item.isDfy && item.postsCount === 0))
      return waiting ? 30000 : false
    },
  })

  // stats and total are page-independent server-side — read both from the first page
  const contentItems = data?.pages.flatMap(page => page.items) ?? []
  const stats = data?.pages[0]?.stats
  const total = data?.pages[0]?.total ?? 0

  const toggleStatusFilter = (status: Exclude<StatusFilter, "all">) => {
    setSelectedStatus(prev => (prev === status ? "all" : status))
  }

  const getSourceTypeIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'youtube':
        return Youtube
      case 'article':
        return FileText
      case 'blog':
        return Globe
      default:
        return Link2
    }
  }

  const getSourceTypeLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'youtube':
        return 'YouTube'
      case 'article':
        return 'Artikel'
      case 'blog':
        return 'Blog'
      default:
        return sourceType
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Fehler beim Laden der Inhalte. Bitte versuchen Sie es später erneut.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <AnimatedSection>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="heading-repurposing-content">
              <Link2 className="h-8 w-8 text-blue-600" />
              Inhalte wiederverwenden
            </h1>
            <p className="text-muted-foreground mt-2">
              KI-generierte Social Media Posts aus YouTube-Videos, Artikeln und Blog-Beiträgen
            </p>
          </div>
          <Button onClick={() => setIsUrlModalOpen(true)} data-testid="button-add-url">
            <Plus className="w-4 h-4 mr-2" />
            URL hinzufügen
          </Button>
        </div>
      </AnimatedSection>

      {/* Stats */}
      <AnimatedSection delay={100}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Gesamt Inhalte"
            value={stats?.total ?? 0}
            hint="Quellen & Lina-Beiträge"
            icon={Link2}
            testId="stat-total-content"
            active={selectedStatus === "all"}
            onClick={() => setSelectedStatus("all")}
          />
          <StatCard
            title="Ausstehend"
            value={stats?.pending ?? 0}
            hint="Noch kein Post veröffentlicht"
            icon={statusConfig.pending.icon}
            testId="stat-pending"
            active={selectedStatus === "pending"}
            onClick={() => toggleStatusFilter("pending")}
          />
          <StatCard
            title="Freigegeben"
            value={stats?.approved ?? 0}
            hint="Mind. ein Post veröffentlicht oder geplant"
            icon={statusConfig.approved.icon}
            testId="stat-approved"
            active={selectedStatus === "approved"}
            onClick={() => toggleStatusFilter("approved")}
          />
          <StatCard
            title="Von Lina"
            value={stats?.dfy ?? 0}
            hint="Service-erstellte Beiträge"
            icon={Sparkles}
            testId="stat-dfy"
          />
        </div>
      </AnimatedSection>

      {/* Filter + Sortierung (same slim row as Industry News) */}
      <AnimatedSection delay={200}>
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Select value={selectedStatus} onValueChange={(v) => setSelectedStatus(v as StatusFilter)}>
            <SelectTrigger className="w-56" data-testid="select-status" aria-label="Nach Status filtern">
              <SelectValue placeholder="Status wählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="pending">Ausstehend</SelectItem>
              <SelectItem value="approved">Freigegeben</SelectItem>
            </SelectContent>
          </Select>
          <ArrowUpDown className="h-4 w-4 text-muted-foreground ml-2" aria-hidden="true" />
          <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as SortOrder)}>
            <SelectTrigger className="w-48" data-testid="select-sort" aria-label="Sortierung">
              <SelectValue placeholder="Sortierung" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Neueste zuerst</SelectItem>
              <SelectItem value="oldest">Älteste zuerst</SelectItem>
            </SelectContent>
          </Select>
          {selectedStatus !== "all" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedStatus("all")}
              data-testid="button-clear-filters"
            >
              Zurücksetzen
            </Button>
          )}
          <span className="text-sm text-muted-foreground ml-auto" data-testid="text-result-count">
            {contentItems.length} von {total} angezeigt
          </span>
        </div>
      </AnimatedSection>

      {/* Content List */}
      <AnimatedSection delay={300}>
        {contentItems.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground">
                {selectedStatus === "all"
                  ? "Noch keine Inhalte vorhanden. Fügen Sie oben einen Link hinzu, um zu starten."
                  : "Keine Inhalte mit diesem Status."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {contentItems.map((content) => {
              const statusInfo = statusConfig[content.derivedStatus]
              const StatusIcon = statusInfo.icon
              const SourceIcon = getSourceTypeIcon(content.sourceType)
              const sourceLabel = getSourceTypeLabel(content.sourceType)

              return (
                <FloatingCard key={content.id} className="hover-elevate">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      {content.thumbnailUrl && (
                        <img
                          src={content.thumbnailUrl}
                          alt={content.title || 'Vorschaubild'}
                          className="w-full aspect-video object-cover rounded-md sm:w-24 sm:h-24 sm:order-2"
                          data-testid={`content-image-${content.id}`}
                        />
                      )}
                      <div className="flex-1 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className={statusInfo.color} data-testid={`badge-status-${content.id}`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusInfo.label}
                          </Badge>
                          {!content.isDfy && (
                            <Badge variant="outline" data-testid={`badge-source-type-${content.id}`}>
                              <SourceIcon className="w-3 h-3 mr-1" />
                              {sourceLabel}
                            </Badge>
                          )}
                          {content.isDfy && (
                            <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200" data-testid={`badge-dfy-${content.id}`}>
                              <Sparkles className="w-3 h-3 mr-1" />
                              Von Lina erstellt
                            </Badge>
                          )}
                          {!content.isDfy && content.nextScheduledFor && content.nextScheduledPostId && (
                            <ScheduledPlanLink
                              postId={content.nextScheduledPostId}
                              scheduledFor={content.nextScheduledFor}
                              testId={`badge-scheduled-${content.id}`}
                            />
                          )}
                          {/* DFY pillar posts DO appear in the Posting-Plan under their own id
                              (plan card DOM id plan-post-<pillarId>), so deep-link there too. */}
                          {content.isDfy && content.nextScheduledFor && (
                            <ScheduledPlanLink
                              postId={content.id}
                              scheduledFor={content.nextScheduledFor}
                              testId={`badge-scheduled-${content.id}`}
                            />
                          )}
                        </div>
                        <CardTitle className="text-xl break-words" data-testid={`content-title-${content.id}`}>
                          {content.title || 'Ohne Titel'}
                        </CardTitle>
                        {content.summary && (
                          <CardDescription className="line-clamp-2" data-testid={`content-summary-${content.id}`}>
                            {content.summary}
                          </CardDescription>
                        )}
                        {content.url && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Link2 className="w-4 h-4" />
                            <a
                              href={content.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-primary hover:underline truncate max-w-md"
                              data-testid={`content-url-${content.id}`}
                            >
                              {content.url}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* DFY (Lina) items are read-only (locked after email approval): view the
                        post + jump to its plan slot, but no edit/publish/delete here. */}
                    {content.isDfy ? (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t">
                        <p className="text-xs text-muted-foreground">
                          Von Lina im Wochenplan erstellt. Bearbeitung und Freigabe erfolgen dort.
                        </p>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => openDfyPost(content.id)}
                          disabled={openingDfyId === content.id}
                          className="w-full sm:w-auto shrink-0"
                          data-testid={`button-view-dfy-${content.id}`}
                        >
                          {openingDfyId === content.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Eye className="w-4 h-4 mr-2" />
                          )}
                          Post ansehen
                        </Button>
                      </div>
                    ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 pt-2 border-t">
                      <div className="flex flex-col sm:flex-row gap-2">
                        {content.url && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(content.url, '_blank', 'noopener,noreferrer')}
                            className="w-full sm:w-auto"
                            data-testid={`button-open-url-${content.id}`}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            URL öffnen
                          </Button>
                        )}
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleViewPosts(content)}
                          disabled={content.postsCount === 0}
                          className="w-full sm:w-auto"
                          data-testid={`button-view-posts-${content.id}`}
                        >
                          {content.postsCount === 0 ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Posts werden erstellt…
                            </>
                          ) : (
                            <>
                              <Eye className="w-4 h-4 mr-2" />
                              Posts ansehen ({content.postsCount})
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteConfirmContent(content)}
                          className="text-destructive hover:text-destructive"
                          aria-label="Inhalt löschen"
                          title="Inhalt löschen"
                          data-testid={`button-delete-${content.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    )}
                  </CardContent>
                </FloatingCard>
              )
            })}

            {hasNextPage && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  data-testid="button-load-more"
                >
                  {isFetchingNextPage ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Mehr laden
                </Button>
              </div>
            )}
          </div>
        )}
      </AnimatedSection>

      {/* Posts Review Modal (self-serve) */}
      {selectedContent && (
        <NewsPostsModal
          open={isModalOpen}
          onOpenChange={handleModalClose}
          newsItem={selectedContent}
          sourceType="repurposing_content"
          initialPlatform={deepLinkPlatform}
        />
      )}

      {/* DFY (Lina) post — read-only view via the shared modal's pillar_post branch */}
      {dfyPost && (
        <NewsPostsModal
          open={isDfyModalOpen}
          onOpenChange={(open) => {
            setIsDfyModalOpen(open)
            if (!open) setDfyPost(null)
          }}
          newsItem={dfyPost}
          sourceType="pillar_post"
          readOnly
        />
      )}

      {/* URL hinzufügen Modal */}
      <RepurposingUrlModal open={isUrlModalOpen} onOpenChange={setIsUrlModalOpen} />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmContent} onOpenChange={(open) => !open && setDeleteConfirmContent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inhalt löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diesen Inhalt wirklich löschen?
              Alle zugehörigen Posts werden ebenfalls gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirmContent && deleteMutation.mutate(deleteConfirmContent.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Löschen..." : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
